import { useState, useEffect } from "react";
import { toast } from "sonner";
import api, { errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Download } from "lucide-react";
import { RoleBadge } from "./UserManagement";

export default function PermissionsMatrix() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [matrix, setMatrix] = useState({});
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    api.get("/permissions").then((r) => { setData(r.data); setMatrix(r.data.matrix); }).catch((e) => toast.error(errMsg(e)));
  }, []);

  if (!data) return null;
  const editable = user.role === "super_admin" ? ["admin", "editor", "viewer"] : ["editor", "viewer"];
  const roles = ["super_admin", "admin", "editor", "viewer"];

  const cycle = (role, key) => {
    if (!editable.includes(role)) return;
    const cur = matrix[role]?.[key];
    const next = cur === "full" ? "read" : cur === "read" ? "none" : "full";
    setMatrix({ ...matrix, [role]: { ...matrix[role], [key]: next } });
    setDirty(true);
  };

  const save = async () => {
    try {
      await api.put("/permissions", { matrix });
      toast.success("Permissions saved — role template updated");
      setDirty(false);
    } catch (e) { toast.error(errMsg(e)); }
  };

  const exportCsv = () => {
    const rows = [["Action", "Key", ...roles]];
    data.groups.forEach((g) => g.actions.forEach((a) => rows.push([a.label, a.key, ...roles.map((r) => r === "super_admin" ? "always" : matrix[r]?.[a.key] || "none")])));
    const csv = rows.map((r) => r.join(",")).join("\n");
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    a.download = "permissions_matrix.csv";
    a.click();
  };

  const Chip = ({ val, always, locked, onClick }) => (
    <button onClick={onClick} disabled={always || locked}
      className={`px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-wide ${
        always ? "bg-[#A25DDC]/15 text-[#A25DDC]" :
        val === "full" ? "bg-[#00C875]/15 text-[#00925a]" :
        val === "read" ? "bg-[#579BFC]/15 text-[#2b6cb8]" : "bg-[#181B34]/6 text-[#9699AD]"} ${locked && !always ? "opacity-50" : ""}`}>
      {always ? "Always" : val === "full" ? "Full" : val === "read" ? "Read" : "—"}
    </button>
  );

  return (
    <div>
      <div className="flex items-center justify-between flex-wrap gap-3">
        <p className="text-sm text-[#676a82] font-medium max-w-lg">Click a cell to cycle Full → Read → None. Super Admin is always granted everything.{user.role === "admin" && " The Admin column is locked for Admins."}</p>
        <div className="flex gap-2">
          <button data-testid="export-matrix-csv" onClick={exportCsv} className="flex items-center gap-1.5 px-3 h-9 rounded-xl bg-white border border-[#181B34]/10 text-xs font-bold"><Download size={13} /> Export CSV</button>
          <button data-testid="save-permissions-btn" onClick={save} disabled={!dirty}
            className="px-5 h-9 rounded-xl bg-[#6161FF] text-white text-xs font-bold disabled:opacity-40">Save changes</button>
        </div>
      </div>
      <div className="mt-4 bg-white rounded-2xl border border-[#181B34]/10 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-[#181B34]/8">
              <th className="px-4 py-3 text-left text-[10px] font-bold uppercase tracking-[0.15em] text-[#9699AD]">Capability</th>
              {roles.map((r) => (
                <th key={r} className="px-3 py-3 text-center">
                  <RoleBadge role={r} />
                  <p className="text-[10px] font-bold text-[#9699AD] mt-1">{data.role_counts[r]} user{data.role_counts[r] === 1 ? "" : "s"}</p>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.groups.map((g) => (
              <GroupRows key={g.group} group={g} roles={roles} matrix={matrix} editable={editable} cycle={cycle} Chip={Chip} />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function GroupRows({ group, roles, matrix, editable, cycle, Chip }) {
  const [open, setOpen] = useState(true);
  return (
    <>
      <tr className="bg-[#F6F7FB] cursor-pointer" onClick={() => setOpen(!open)}>
        <td colSpan={5} className="px-4 py-2 font-black text-xs uppercase tracking-[0.15em] text-[#4b4e63]">
          {open ? "▾" : "▸"} {group.group} <span className="text-[#9699AD] normal-case font-bold">· {group.actions.length} actions</span>
        </td>
      </tr>
      {open && group.actions.map((a) => (
        <tr key={a.key} className="border-b border-[#181B34]/5" data-testid={`perm-row-${a.key}`}>
          <td className="px-4 py-2.5">
            <p className="font-bold text-[13px]">{a.label}</p>
            <p className="text-[10px] font-mono text-[#9699AD]">{a.key}</p>
          </td>
          {roles.map((r) => (
            <td key={r} className="px-3 py-2.5 text-center">
              <Chip val={r === "super_admin" ? "full" : matrix[r]?.[a.key] || "none"} always={r === "super_admin"}
                locked={!editable.includes(r)} onClick={() => cycle(r, a.key)} />
            </td>
          ))}
        </tr>
      ))}
    </>
  );
}
